import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Config } from '../../../App';
import { getAllAvailableFonts } from '../../../utils/fontUtils';

interface FontsEditorProps {
  config: Config | null;
  onThemeChange: (key: string, value: any) => void;
}

const DEFAULT_FONTS = [
  {
    label: 'Default',
    value: '"Roboto", "Helvetica", "Arial", sans-serif',
    isCustom: false,
  },
];

const ColorInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => (
  <TextField
    label={label}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    fullWidth
    variant="outlined"
    size="small"
    sx={{ mb: 2 }}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <Box
            component="input"
            type="color"
            value={value || '#000000'}
            onChange={(e: any) => onChange(e.target.value)}
            sx={{
              width: '24px',
              height: '24px',
              border: 'none',
              padding: 0,
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
          />
        </InputAdornment>
      ),
    }}
  />
);

const FontControl: React.FC<{
  label: string;
  variant: string;
  config: Config;
  fontOptions: { label: string; value: string; isCustom: boolean }[];
  onChange: (key: string, value: any) => void;
}> = ({ label, variant, config, fontOptions, onChange }) => {
  const typography = config.theme.typography || {};
  const variantStyle = typography[variant] || {};

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        {label}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Font family</InputLabel>
            <Select
              label="Font family"
              value={variantStyle.fontFamily || fontOptions[0]?.value}
              onChange={(e) =>
                onChange(`typography.${variant}.fontFamily`, e.target.value)
              }
            >
              {fontOptions.map((font) => (
                <MenuItem key={font.label} value={font.value}>
                  {font.label} {font.isCustom && '(Custom)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Size"
            size="small"
            fullWidth
            value={variantStyle.fontSize || ''}
            onChange={(e) =>
              onChange(`typography.${variant}.fontSize`, e.target.value)
            }
            placeholder="e.g. 2rem, 16px"
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <TextField
            label="Font Weight"
            size="small"
            fullWidth
            value={variantStyle.fontWeight || ''}
            onChange={(e) =>
              onChange(`typography.${variant}.fontWeight`, e.target.value)
            }
            placeholder="400, 700"
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <TextField
            label="Line height"
            size="small"
            fullWidth
            value={variantStyle.lineHeight || ''}
            onChange={(e) =>
              onChange(`typography.${variant}.lineHeight`, e.target.value)
            }
            placeholder="1.5"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const FontsEditor: React.FC<FontsEditorProps> = ({ config, onThemeChange }) => {
  const [fontOptions, setFontOptions] =
    useState<{ label: string; value: string; isCustom: boolean }[]>(
      DEFAULT_FONTS
    );

  useEffect(() => {
    const loadFonts = async () => {
      const fonts = await getAllAvailableFonts();
      setFontOptions(fonts);
    };
    loadFonts();
  }, []);

  if (!config) return null;

  const handleGlobalHeadingChange = (key: string, value: any) => {
    ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].forEach((variant) => {
      onThemeChange(`typography.${variant}.${key}`, value);
    });
  };

  const handleGlobalBodyChange = (key: string, value: any) => {
    ['body1', 'body2'].forEach((variant) => {
      onThemeChange(`typography.${variant}.${key}`, value);
    });
  };

  return (
    <Grid container spacing={3}>
      {/* HEADINGS */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="subtitle2" sx={{ mb: 3 }}>
            HEADINGS
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Font family</InputLabel>
              <Select
                label="Font family"
                value={config.theme.typography?.h1?.fontFamily || ''}
                onChange={(e) =>
                  handleGlobalHeadingChange('fontFamily', e.target.value)
                }
              >
                {fontOptions.map((font) => (
                  <MenuItem key={font.label} value={font.value}>
                    {font.label} {font.isCustom && '(Custom)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Checkbox />}
              label={
                <Typography variant="caption">Apply to all headings</Typography>
              }
            />
          </Box>

          <Accordion
            defaultExpanded
            elevation={0}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <Typography variant="subtitle2">Headings</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <FontControl
                label="HEADER 1"
                variant="h1"
                config={config}
                fontOptions={fontOptions}
                onChange={onThemeChange}
              />
              <FontControl
                label="HEADER 2"
                variant="h2"
                config={config}
                fontOptions={fontOptions}
                onChange={onThemeChange}
              />
              <FontControl
                label="HEADER 3"
                variant="h3"
                config={config}
                fontOptions={fontOptions}
                onChange={onThemeChange}
              />
              <FontControl
                label="HEADER 4"
                variant="h4"
                config={config}
                fontOptions={fontOptions}
                onChange={onThemeChange}
              />
              <FontControl
                label="HEADER 5"
                variant="h5"
                config={config}
                fontOptions={fontOptions}
                onChange={onThemeChange}
              />
              <FontControl
                label="HEADER 6"
                variant="h6"
                config={config}
                fontOptions={fontOptions}
                onChange={onThemeChange}
              />
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Grid>

      {/* BODY */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="subtitle2" sx={{ mb: 3 }}>
            BODY
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Font family</InputLabel>
              <Select
                label="Font family"
                value={config.theme.typography?.body1?.fontFamily || ''}
                onChange={(e) =>
                  handleGlobalBodyChange('fontFamily', e.target.value)
                }
              >
                {fontOptions.map((font) => (
                  <MenuItem key={font.label} value={font.value}>
                    {font.label} {font.isCustom && '(Custom)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Checkbox />}
              label={
                <Typography variant="caption">
                  Apply to all body text
                </Typography>
              }
            />
          </Box>

          <Accordion
            defaultExpanded
            elevation={0}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <Typography variant="subtitle2">Body texts</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <FontControl
                label="BODY1"
                variant="body1"
                config={config}
                fontOptions={fontOptions}
                onChange={onThemeChange}
              />
              <FontControl
                label="BODY2"
                variant="body2"
                config={config}
                fontOptions={fontOptions}
                onChange={onThemeChange}
              />
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Grid>

      {/* OTHER */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 3 }}>
              BUTTON
            </Typography>
            <FontControl
              label=""
              variant="button"
              config={config}
              fontOptions={fontOptions}
              onChange={onThemeChange}
            />
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 3 }}>
              TEXTLINK
            </Typography>
            {/* Assuming subtitle1 for textlink for now, or maybe we need a custom variant */}
            <FontControl
              label=""
              variant="subtitle1"
              config={config}
              fontOptions={fontOptions}
              onChange={onThemeChange}
            />
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 3 }}>
              CAPTION
            </Typography>
            <FontControl
              label=""
              variant="caption"
              config={config}
              fontOptions={fontOptions}
              onChange={onThemeChange}
            />
          </Paper>
        </Box>
      </Grid>
    </Grid>
  );
};

export default FontsEditor;
